import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export type Effects = 'glass' | 'flat';

export interface EffectsContextValue {
  effects: Effects;
  setEffects: (effects: Effects) => void;
  toggleEffects: () => void;
  isGlass: boolean;
  isFlat: boolean;
}

export interface EffectsProviderProps {
  children: ReactNode;
  defaultEffects?: Effects;
  storageKey?: string;
  disablePersistence?: boolean;
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

const REDUCED_TRANSPARENCY_QUERY = '(prefers-reduced-transparency: reduce)';

function getSystemPreference(): Effects | null {
  if (typeof window === 'undefined' || !window.matchMedia) return null;
  return window.matchMedia(REDUCED_TRANSPARENCY_QUERY).matches ? 'flat' : null;
}

// -----------------------------------------------------------------------------
// Context
// -----------------------------------------------------------------------------

const EffectsContext = createContext<EffectsContextValue | null>(null);

// -----------------------------------------------------------------------------
// Provider
// -----------------------------------------------------------------------------

export function EffectsProvider({
  children,
  defaultEffects = 'glass',
  storageKey = 'core-effects',
  disablePersistence = false,
}: EffectsProviderProps) {
  const [effects, setEffectsState] = useState<Effects>(() => {
    if (!disablePersistence) {
      try {
        const stored = localStorage.getItem(storageKey) as Effects | null;
        if (stored === 'glass' || stored === 'flat') return stored;
      } catch {
        // localStorage not available
      }
    }

    return getSystemPreference() ?? defaultEffects;
  });

  // Apply effects attribute to DOM and persist
  useEffect(() => {
    document.documentElement.setAttribute('data-effects', effects);

    if (!disablePersistence) {
      try {
        localStorage.setItem(storageKey, effects);
      } catch {
        // localStorage not available
      }
    }
  }, [effects, storageKey, disablePersistence]);

  // Listen for OS-level reduced-transparency changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mql = window.matchMedia(REDUCED_TRANSPARENCY_QUERY);

    const handleChange = (e: MediaQueryListEvent) => {
      setEffectsState(e.matches ? 'flat' : 'glass');
    };

    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  const setEffects = useCallback((next: Effects) => {
    setEffectsState(next);
  }, []);

  const toggleEffects = useCallback(() => {
    setEffectsState((prev) => (prev === 'glass' ? 'flat' : 'glass'));
  }, []);

  const value: EffectsContextValue = {
    effects,
    setEffects,
    toggleEffects,
    isGlass: effects === 'glass',
    isFlat: effects === 'flat',
  };

  return (
    <EffectsContext.Provider value={value}>{children}</EffectsContext.Provider>
  );
}

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

export function useEffects(): EffectsContextValue {
  const context = useContext(EffectsContext);

  if (!context) {
    throw new Error(
      'useEffects must be used within an EffectsProvider. ' +
        'Wrap your app in <EffectsProvider> to use this hook.'
    );
  }

  return context;
}

// -----------------------------------------------------------------------------
// Utility: Get effects mode from DOM without provider
// -----------------------------------------------------------------------------

export function getEffectsFromDOM(): Effects {
  if (typeof document === 'undefined') return 'glass';
  const attr = document.documentElement.getAttribute('data-effects');
  return attr === 'flat' ? 'flat' : 'glass';
}

export default EffectsProvider;
